import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { COMPANY, waLink, WA_MESSAGES } from "@/data/company";
import { SERVICES, SPECIAL_SOLUTIONS } from "@/data/services";
import { CTAButton, MoveCare } from "@/components/common/Bits";
import { WindowGrid } from "@/components/motion/Reveal";

const companyLinks = [
    { label: "About Us", to: "/about-us" },
    { label: "Our Works", to: "/our-works" },
    { label: "Testimonials", to: "/testimonials" },
    { label: "Blog", to: "/blog" },
    { label: "Contact", to: "/contact" },
];

const legalLinks = [
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms of Use", to: "/terms-of-use" },
    { label: "Sitemap", to: "/sitemap" },
];

export default function Footer() {
    return (
        <footer className="relative overflow-hidden bg-brand-deep text-white grain" data-testid="footer">
            <div className="pointer-events-none absolute -right-10 top-10 opacity-[0.06]">
                <WindowGrid size={220} />
            </div>

            {/* Big CTA band */}
            <div className="relative border-b border-white/10">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center">
                    <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                        <MoveCare text="Planning a MOVE?" />
                        <br />
                        <span className="text-white/70">Leave the </span>
                        <MoveCare text="CARE" />
                        <span className="text-white/70"> to us.</span>
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        <CTAButton href={waLink(WA_MESSAGES.quote)} whatsapp testid="footer-whatsapp-cta">
                            WhatsApp Us Now
                        </CTAButton>
                        <CTAButton to="/contact" variant="light" testid="footer-contact-cta">
                            Request a Quotation
                        </CTAButton>
                    </div>
                </div>
            </div>

            {/* Link columns */}
            <div className="relative mx-auto grid max-w-7xl gap-10 px-6 py-16 sm:grid-cols-2 lg:grid-cols-5">
                <div className="sm:col-span-2 lg:col-span-1">
                    <div className="flex items-center gap-2">
                        <img src={COMPANY.logo} alt="ProCare.SG" className="h-12 w-12 object-contain" />
                        <span className="font-display text-xl font-semibold">
                            <span className="text-white">Pro</span>
                            <span className="text-brand-red">Care</span>
                            <span className="text-white">.SG</span>
                        </span>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-white/60">
                        One-stop property care for homes and businesses across Singapore.
                    </p>
                    <p className="mt-4 font-display text-lg">
                        <MoveCare text="Your MOVE. Our CARE." />
                    </p>
                </div>

                <div>
                    <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Company</h2>
                    <ul className="space-y-2.5 text-sm text-white/70">
                        {companyLinks.map((l) => (
                            <li key={l.to}>
                                <Link to={l.to} className="transition-colors hover:text-white">{l.label}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Services</h2>
                    <ul className="space-y-2.5 text-sm text-white/70">
                        {SERVICES.map((s) => (
                            <li key={s.slug}>
                                <Link to={`/services/${s.slug}`} className="transition-colors hover:text-white">{s.name}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Other Solutions</h2>
                    <ul className="space-y-2.5 text-sm text-white/70">
                        {SPECIAL_SOLUTIONS.map((s) => (
                            <li key={s.slug}>
                                <Link to={`/special-solutions/${s.slug}`} className="transition-colors hover:text-white">{s.name}</Link>
                            </li>
                        ))}
                    </ul>
                    <h2 className="mb-4 mt-6 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Legal</h2>
                    <ul className="space-y-2.5 text-sm text-white/70">
                        {legalLinks.map((l) => (
                            <li key={l.to}>
                                <Link to={l.to} className="transition-colors hover:text-white">{l.label}</Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Contact</h2>
                    <ul className="space-y-3 text-sm text-white/70">
                        <li className="flex items-start gap-2">
                            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                            <a href={`tel:${COMPANY.mobileRaw}`} className="hover:text-white">{COMPANY.mobile}</a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                            <a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a>
                        </li>
                        <li className="flex items-start gap-2">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                            <span>
                                {COMPANY.address.line1}, {COMPANY.address.line2}<br />
                                {COMPANY.address.line3}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="relative border-t border-white/10">
                <div className="mx-auto flex max-w-7xl items-center justify-center px-6 py-6 text-xs text-white/60">
                    <p>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
}
